import { useInstantFormat } from './instant-format';
import { useI18n } from '@platform/i18n';
import { Alert, Button, toast, useErrorText } from '@platform/ui';
import { MailXIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, type AutomaticReplies } from './api';
import { publishAutomaticReplies } from './automatic-replies-store';

/** Banner of the mail view while automatic replies are being sent; "Turn off" saves the same settings with state off. */
export function AutomaticRepliesBanner({ replies }: { replies: AutomaticReplies | undefined }) {
  const { t } = useI18n();
  const formatDate = useInstantFormat();
  const errors = useErrorText();
  const [busy, setBusy] = useState(false);
  if (!replies?.active) return null;

  const turnOff = async () => {
    setBusy(true);
    try {
      const { start, end, internalHtml, externalHtml, externalAudience } = replies;
      publishAutomaticReplies(await api.setAutomaticReplies({ state: 'off', start, end, internalHtml, externalHtml, externalAudience }));
      toast.success(t('webmail.automaticReplies.turnedOff'));
    } catch (error) {
      errors.notify(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Alert variant="info" data-testid="automatic-replies-banner" className="shrink-0 items-center rounded-none border-x-0 border-t-0 py-2">
      <MailXIcon aria-hidden />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 basis-48">
          {replies.state === 'scheduled' && replies.end
            ? t('webmail.automaticReplies.bannerUntil', { end: formatDate(replies.end) })
            : t('webmail.automaticReplies.banner')}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/settings/automatic-replies">{t('webmail.automaticReplies.bannerEdit')}</Link>
          </Button>
          <Button size="sm" onClick={() => void turnOff()} disabled={busy}>
            {t('webmail.automaticReplies.turnOff')}
          </Button>
        </div>
      </div>
    </Alert>
  );
}
